import React, { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { SectionIndicatorContext } from "../../shared/contexts/SectionIndicatorContext";
import FooterButtons from "../../components/FooterButtons.";
import EmailVerification from "../../pages/EmailFlowPswResetPages/EmailVerification";
import EmailFlowPswResetSuccess from "../../pages/EmailFlowPswResetPages/EmailFlowPswResetSuccess";
import { useSearchParams } from "react-router-dom";
import SetPassword from "../../pages/SetPassword/SetPassword";
import { Alert, Link, Typography } from "@mui/material";
import { emailDto, getSetPswDto, tokenDto } from "../../shared/utils/dto-mapper";
import { emailSend, emailVerify, setPassword } from "../../api/api-calls";
import AccountSelect from "../../pages/AccountSelect/AccountSelect";
import Loading from "../../components/Loading/Loading";
import SetPasswordSuccess from "../../pages/SetPassword/SetPasswordSuccess/SetPasswordSuccess";
import FormLayout from "../../layoutComponents/FormLayout";
import FormLayoutBody from "../../layoutComponents/FormLayout/FormLayoutBody";
import FormLayoutActions from "../../layoutComponents/FormLayout/FormLayoutActions";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { baseColors } from "../../theme";
import { getExternalUrls } from "../../configuration/config-external-urls";
import { useWebViewContext } from "../../components/WebViewProvider/webViewContext";

const EmailFlowPswResetLayout = () => {
	const { setSectionNumber, sectionNumber } = useContext(SectionIndicatorContext);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [users, setUsers] = useState<string[]>([]);
	const [searchParams] = useSearchParams();
	const token = searchParams.get("apt_val");
	const [isInitAppLoading, setIsInitAppLoading] = useState<boolean>(false);
	const { isWebView } = useWebViewContext();
	const origin = isWebView ? "mobile-app" : "web-app"

	const methods = useForm({
		defaultValues: {
			account: users.length ? "" : users[0],
			email: "",
			password: "",
			passwordAgain: "",
		},
	});

	const {
		handleSubmit,
		reset,
		getValues,
		setError,
		setValue,
		formState: { errors },
	} = methods;

	useEffect(() => {
		if (token) {
			const tokenDTO = tokenDto(token);
			emailVerify(tokenDTO, setValue, setUsers, setError, setSectionNumber, setIsInitAppLoading, "email-verify");
		} else {
			setSectionNumber(1);
		}
	}, []);

	function onSubmit(event?: any) {
		event.preventDefault();
		handleSubmit((data: any) => {
			switch (sectionNumber) {
				case 1:
					const emailDTO = emailDto(data.email, origin);
					emailSend(emailDTO, setError, setSectionNumber, setIsLoading, "email-send");
					break;
				case 5:
					setSectionNumber(6);
					break;
				case 6:
					if (!token) {
						return;
					}
					const pswDTO = getSetPswDto(token, "token", data.account, data.password);
					setPassword(pswDTO, setError, setIsLoading, setSectionNumber, "set-password");
					break;
				case 2:
				case 7:
					window.open(getExternalUrls().LOGIN_PAGE_URL);
					break;
			}
		})();
	}

	function sendSecondEmail() {
		const emailDTO = emailDto(getValues("email"), origin);
		emailSend(emailDTO, setError, setSectionNumber, setIsLoading, "email-send");
	}

	function switchPages(sectionNumber: number | null) {
		switch (sectionNumber) {
			case 1:
				return <EmailVerification />;
			case 2:
				return <EmailFlowPswResetSuccess />;
			case 5:
				//waits for users account to load
				return users.length ? <AccountSelect defaultValue={users[0]} users={users} /> : <Loading />;
			case 6:
				return <SetPassword />;
			case 7:
				return <SetPasswordSuccess />;
		}
	}

	return (
		<FormLayout>
			<form style={{ display: "flex", flexDirection: "column", flexGrow: 1 }} onSubmit={onSubmit}>
				<FormProvider {...methods}>
					<FormLayoutBody>
						{isInitAppLoading ? <Loading /> : switchPages(sectionNumber)}
						{errors.root?.serverError.type === "success" && (
							<Alert variant="filled" severity="success" icon={<CheckCircleOutlineIcon fontSize="large" />}>
								{errors.root?.serverError.message}
							</Alert>
						)}
						{errors.root?.serverError.type === "general" && (
							<>
								{errors.root?.serverError.message && errors.root?.serverError.message.includes("RATELIMIT_001") ? (
									<Typography color={baseColors.red} align="center" variant="body1" gutterBottom>
										<p>
											Překročili jste maximální počet pokusů pro obnovu hesla. Opakujte prosím akci o několik minut
											později.
										</p>
										<p>&nbsp;</p>
										<p>
											V případě přetrvávajících potíží s obnovou hesla nás kontaktujte na naší zákaznické lince v
											pracovních dnech od 8 do 18 hodin na čísle{" "}
											<Link color={baseColors.orange} underline="always" tabIndex={-1} href="tel:+420800850860">
												800 850 860
											</Link>
											.
										</p>
									</Typography>
								) : (
									<Typography color={baseColors.red} align="center" variant="body1" gutterBottom>
										{errors.root?.serverError.message}
									</Typography>
								)}
							</>
						)}
					</FormLayoutBody>
					<FormLayoutActions>
						<FooterButtons
							reset={reset}
							isLoading={isLoading}
							isInitAppLoading={isInitAppLoading}
							sendSecondEmail={sendSecondEmail}
						></FooterButtons>
					</FormLayoutActions>
				</FormProvider>
			</form>
		</FormLayout>
	);
};

export default EmailFlowPswResetLayout;
